import { useEffect } from 'react'
import Lenis from 'lenis'
import { gsap, ScrollTrigger } from './gsap'
import { usePrefersReducedMotion } from './useReducedMotion'

/**
 * Lenis smooth scroll wired into the GSAP ticker so ScrollTrigger stays in sync.
 * Mount once per page. Disabled under prefers-reduced-motion.
 */
export function useSmoothScroll(enabled = true) {
  const reduced = usePrefersReducedMotion()
  useEffect(() => {
    if (!enabled || reduced) return

    const lenis = new Lenis({
      duration: 1.1,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    })

    lenis.on('scroll', ScrollTrigger.update)
    const tick = (time: number) => lenis.raf(time * 1000)
    gsap.ticker.add(tick)
    gsap.ticker.lagSmoothing(0)

    // On client-side route changes the page's ScrollTriggers are created (in
    // child effects) BEFORE this Lenis instance exists, so they latch onto a
    // torn-down scroller and stop updating. Refresh once Lenis is live and the
    // layout has settled so scroll-driven sections (the home hero) work again.
    const raf = requestAnimationFrame(() => ScrollTrigger.refresh())
    // Client-side navigations never fire a window `load` event, so triggers
    // measured before late images/fonts settle would stay stale. One delayed
    // refresh catches that layout shift.
    const lateRefresh = window.setTimeout(() => ScrollTrigger.refresh(), 600)

    // anchor links → smooth scroll
    const onClick = (e: MouseEvent) => {
      const t = (e.target as HTMLElement)?.closest('a[href^="#"]') as HTMLAnchorElement | null
      if (!t) return
      const id = t.getAttribute('href')!.slice(1)
      if (!id) return
      const el = document.getElementById(id)
      if (el) {
        e.preventDefault()
        lenis.scrollTo(el, { offset: -70 })
      }
    }
    document.addEventListener('click', onClick)

    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(lateRefresh)
      document.removeEventListener('click', onClick)
      gsap.ticker.remove(tick)
      lenis.destroy()
      ScrollTrigger.getAll().forEach((s) => s.kill())
    }
  }, [enabled, reduced])
}
